/**
 * 바이브 코딩 도우미(2026-09-28 대표 "업그레이드 해볼까!")
 *   ① suggestChips(ir, ctx) — 방금 만든 것에 맞춘 "다음에 말해 볼 것" 예시 버튼.
 *      후보를 고른 뒤 **실제로 고쳐 말하기(refine)를 돌려서 되는 것만** 남긴다 → 눌렀는데 "모르겠어요"인 버튼은 없다.
 *   ② explainIr(ir, lang) — "이거 어떻게 동작해?" → 방금 만든 블록을 위에서부터 한 줄씩 쉬운 말로.
 *      블록 이름(describe 줄)을 존댓말 문장으로 바꾸고, 자주 나오는 묶음(두 바퀴 돌리기·점프)은 뜻으로 풀어 준다.
 *   isExplainRequest(text, lang) — 설명해 달라는 말인가.
 * 되돌리기: vibe-panel.jsx 에서 이 파일 import 와 suggestChips·explain 호출을 빼면 예전(고정 예시 줄) 그대로.
 */
'use strict';
const core = require('./vibe-core.js');

// ─────────────────────────────── ① 상황 맞춤 예시 ───────────────────────────────
const opsAll = ir => core._kit.opsOf(ir).concat(ir.scripts.map(sc => (sc.hat && sc.hat.opcode) || ''));
const has = (ops, re) => ops.some(op => re.test(op));
// [조건, 후보 목록] — 위에서부터 모은다(중복 제거). 후보는 전부 실제 refine 으로 걸러진다.
const CANDS = {
    ko: [
        [o => has(o, /^cubroidweather_/), ['부산으로 바꿔', '비가 오면 우산 챙기라고 말해', '일주일 기온 차트 보여줘']],
        [o => has(o, /^cubroidchart_/), ['평균 말해', '표로 보여줘', '원 그래프 보여줘']],
        [o => has(o, /^cubroidChatgpt_/), ['2초 동안만 말해']],
        [o => has(o, /^facerecognition_/), ['슬프면 멈춰', '얼굴이 보이면 안녕 말해']],
        [o => has(o, /^(?:cubroidcolor|cubroidobject)_/), ['감지된 색 말해', '사물 인식해서 이름 말해']],
        [o => has(o, /_dcMotorControl$/) && !has(o, /^cubroidproximity_/), ['장애물 있으면 멈추게 해줘', '더 빨리', '끝나면 삐 소리 내줘', '3번 반복해']],
        [o => has(o, /_dcMotorControl$/) && has(o, /^cubroidproximity_/), ['더 빨리', '장애물 있으면 삐 소리도 내줘', '속도 50%로 해']],
        [o => has(o, /^cubroidled_/), ['깜빡이게 해줘', '하트로 바꿔', '끝나면 LED 지워줘']],
        [o => has(o, /^cubroidmaster_cubroidMasterColorControl$/), ['파란색으로 바꿔', '깜빡이게 해줘', '1초 뒤에 꺼줘']],
        [o => has(o, /^cubroidsound_soundBeatControl$/), ['더 빠르게', '2번 반복해', '거꾸로도 연주해줘']],
        [o => has(o, /^pen_/), ['빨간색으로 그려줘', '더 크게', '다 그리면 야옹 소리 내줘']],
        [o => has(o, /^motion_(?:movesteps|changexby|changeyby|glide|turn)/), ['더 빨리', '계속 반복해', '벽에 닿으면 튕기게 해줘', '그리고 소리도 내줘']],
        [o => has(o, /^looks_say/), ['2초 동안만 말해', '그리고 소리도 내줘', '계속 반복해']],
        [() => true, ['더 빨리', '계속 반복해', '그리고 소리도 내줘']]
    ],
    en: [
        [o => has(o, /^cubroidweather_/), ['change it to London', 'if it rains say take an umbrella']],
        [o => has(o, /^cubroidchart_/), ['say the average', 'show table', 'hide the chart']],
        [o => has(o, /_dcMotorControl$/) && !has(o, /^cubroidproximity_/), ['stop if there is an obstacle', 'faster', 'beep at the end', '3 times']],
        [o => has(o, /_dcMotorControl$/), ['faster', 'slower']],
        [o => has(o, /^cubroidled_/), ['make it blink', 'show a heart instead']],
        [o => has(o, /^cubroidmaster_cubroidMasterColorControl$/), ['make it blue', 'make it blink']],
        [o => has(o, /^motion_(?:movesteps|changexby|changeyby|glide|turn)/), ['faster', 'repeat forever', 'bounce off the edge', 'also play a sound']],
        [() => true, ['faster', 'repeat forever', 'also play a sound']]
    ]
};
// 2026-10-16 직원 의견 '되돌리기 단어가 애매' → 칩도 하는 일이 보이는 말로('방금 거 취소해' = 되돌리기로 알아들음, 확인함)
const TAIL = {ko: ['어떻게 동작해?', '방금 거 취소해'], en: ['how does it work?', 'undo']};
const sigOf = ir => { try { return core.signature(ir); } catch (e) { return JSON.stringify(ir.scripts); } };
/**
 * @param {object} ir 지금 만든 설계도
 * @param {object} ctx translate 와 같은 ctx({lang, soundNames, …})
 * @param {number} [max] 최대 개수(끝의 "어떻게 동작해?"·"방금 거 취소해" 빼고)
 * @returns {string[]} 눌러서 되는 예시만
 */
const suggestChips = (ir, ctx, max) => {
    const lang = ctx && ctx.lang === 'en' ? 'en' : 'ko';
    if (!ir || !ir.scripts || !ir.scripts.length) return [];
    const ops = opsAll(ir);
    const cands = [];
    CANDS[lang].forEach(([when, list]) => { if (when(ops)) list.forEach(x => { if (cands.indexOf(x) < 0) cands.push(x); }); });
    const before = sigOf(ir);
    const ok = [];
    for (const c of cands) {
        if (ok.length >= (max || 4)) break;
        let r = null;
        try { r = core.refine(ir, c, ctx); } catch (e) { r = null; }
        // 고쳐 말하기로 바뀌고(블록이 달라짐) 모르는 말이 늘지 않은 것만
        if (r && r.ir && r.kind !== 'undo' && r.ir.unparsed.length <= ir.unparsed.length && sigOf(r.ir) !== before) ok.push(c);
    }
    return ok.concat(TAIL[lang]);
};

// ─────────────────────────────── ② 블록 설명 ───────────────────────────────
const RE_EXPLAIN = {
    ko: /설명|어떻게\s*(?:동작|작동|돌아가|움직이|되는|된\s*거|하는\s*거)|무슨\s*뜻|뭘?\s*하는\s*(?:거|건)|한\s*줄씩|왜\s*이렇게\s*(?:만들|됐|돼|된)|이게\s*뭐(?:야|예요|에요)?\s*[?？]?$/,
    en: /\bexplain\b|how\s+does\s+(?:it|this|that)\s+work|what\s+does\s+(?:it|this|that)\s+do|walk\s+me\s+through|what\s+do\s+(?:these|the)\s+blocks\s+do/i
};
const isExplainRequest = (text, lang) => RE_EXPLAIN[lang === 'en' ? 'en' : 'ko'].test(String(text || '').trim());

// 블록 이름("…하기") → 존댓말 문장("…해요.")
const CONJ = [
    [/보여주기$/, '보여줘요'], [/기다리기$/, '기다려요'], [/돌리기$/, '돌려요'], [/돌기$/, '돌아요'], [/켜기$/, '켜요'], [/끄기$/, '꺼요'], [/보이기$/, '보여요'],
    [/바꾸기$/, '바꿔요'], [/움직이기$/, '움직여요'], [/숨기기$/, '숨겨요'], [/내기$/, '내요'], [/지우기$/, '지워요'], [/튕기기$/, '튕겨요'], [/불러오기$/, '불러와요'],
    [/내리기$/, '내려요'], [/올리기$/, '올려요'], [/찍기$/, '찍어요'], [/보기$/, '봐요'], [/시작하기$/, '시작해요'], [/시작$/, '시작해요'], [/정지$/, '멈춰요'],
    [/하기$/, '해요'], [/보내기$/, '보내요'], [/멈추기 \(모두\)$/, '모든 걸 멈춰요'], [/복제하기$/, '복제해요'], [/삭제하기$/, '삭제해요']
];
// 자주 나오는 블록은 뜻이 드러나는 문장으로(“막대 차트 보여요” 같은 어색한 활용 대신)
const SPECIAL = [
    [/^(.+) 차트 보이기$/, '$1 차트를 무대에 그려서 보여 줘요.'], [/^표 보이기$/, '데이터를 표로 무대에 보여 줘요.'], [/^차트 숨기기$/, '차트를 무대에서 치워요.'],
    [/^데이터 모두 지우기$/, '차트 데이터를 모두 지워요(새로 시작).'], [/^(.+?) 에 (.+) 추가하기$/, '‘$1’ 칸에 $2을(를) 데이터로 더해요.'],
    [/^(.+) 날씨 불러오기$/, '$1의 지금 날씨를 인터넷에서 불러와요.'], [/^(.+) 끝까지 재생하기$/, '‘$1’ 소리를 끝까지 내요.'], [/^(.+) 재생하기$/, '‘$1’ 소리를 내요.'],
    [/^(.+?) 불빛 켜기$/, '로봇에 $1 불빛을 켜요.'], [/^불빛 끄기$/, '로봇 불빛을 꺼요.'], [/^보이기$/, '스프라이트를 보이게 해요.'], [/^숨기기$/, '스프라이트를 숨겨요.'],
    [/^(.+) 인식 시작(?:하기)?$/, '카메라를 켜고 $1 인식을 시작해요.'], [/^이미지 생성 프롬프트 (.+)$/, 'AI에게 ‘$1’ 그림을 그려 달라고 해요.']
];
const conj = label => {
    const s = String(label || '').trim();
    for (const [re, to] of SPECIAL) if (re.test(s)) return s.replace(re, to);
    for (const [re, to] of CONJ) if (re.test(s)) return `${s.replace(re, to)}.`;
    return `‘${s}’ 블록을 실행해요.`;
};
const hatKo = label => {
    const s = String(label || '');
    if (/깃발을 클릭했을 때/.test(s)) return '초록 깃발을 누르면 시작해요.';
    let m = /^(.+?) 키를 눌렀을 때$/.exec(s);
    if (m) return `${m[1]} 키를 누를 때마다 시작해요.`;
    if (/스프라이트를 클릭했을 때/.test(s)) return '스프라이트를 클릭하면 시작해요.';
    if (/복제되었을 때/.test(s)) return '복제본이 하나 생길 때마다, 그 복제본이 아래 일을 해요.';
    m = /^수신된 레이블: (.+)$/.exec(s);
    if (m) return `머신러닝이 카메라에서 레이블 ${m[1]}을(를) 알아볼 때마다 시작해요.`;
    m = /^(.+?) 신호를 받았을 때$/.exec(s);
    if (m) return `‘${m[1]}’ 신호를 받으면 시작해요.`;
    return `‘${s}’ — 이때 시작해요.`;
};
const wrapKo = label => {
    const s = String(label || '');
    if (s === '무한 반복하기') return '여기부터는 멈출 때까지 계속 되풀이해요:';
    let m = /^(\d+)번 반복하기$/.exec(s);
    if (m) return `${m[1]}번 되풀이해요:`;
    m = /^‘(.*)’ 까지 반복하기$/.exec(s);
    if (m) return `‘${m[1]}’이(가) 될 때까지 되풀이해요:`;
    m = /^만약 ‘(.*)’ 라면$/.exec(s);
    if (m) return `만약 ‘${m[1]}’이면:`;
    if (s === '아니면') return '그렇지 않으면:';
    return null;
};
// 모터 두 줄(1번·2번)을 뜻으로 — 앞으로 = 1번 오른쪽 + 2번 왼쪽(로봇 규칙)
const motorPair = (a, b) => {
    const r = /DC 모터 1번을 (오른쪽|왼쪽)으로 돌리기/.exec(a);
    const r2 = /DC 모터 2번을 (오른쪽|왼쪽)으로 돌리기/.exec(b);
    if (r && r2) {
        if (r[1] === '오른쪽' && r2[1] === '왼쪽') return '두 바퀴를 서로 반대로 돌려서 앞으로 가요.';
        if (r[1] === '왼쪽' && r2[1] === '오른쪽') return '두 바퀴를 서로 반대로 돌려서 뒤로 가요.';
        return `두 바퀴를 같은 방향으로 돌려서 제자리에서 ${r[1] === '오른쪽' ? '오른쪽' : '왼쪽'}으로 돌아요.`;
    }
    if (/DC 모터 1번을 끄기/.test(a) && /DC 모터 2번을 끄기/.test(b)) return '두 바퀴를 멈춰요.';
    return null;
};
const explainKo = ir => {
    const lines = [];
    core.describe(ir).forEach((rows, si) => {
        if (ir.scripts.length > 1) lines.push({depth: 0, text: `스크립트 ${si + 1}`, head: true});
        for (let i = 0; i < rows.length; i++) {
            const r = rows[i];
            if (i === 0) { lines.push({depth: 0, text: hatKo(r.label)}); continue; }
            const w = wrapKo(r.label);
            if (w) { lines.push({depth: r.depth, text: w}); continue; }
            const nx = rows[i + 1];
            const nx2 = rows[i + 2];
            const pair = nx && nx.depth === r.depth ? motorPair(r.label, nx.label) : null;
            if (pair) {
                const wt = nx2 && nx2.depth === r.depth ? /^(\S+)초 기다리기$/.exec(nx2.label) : null;
                if (wt && !/멈춰요/.test(pair)) { lines.push({depth: r.depth, text: `${pair.replace(/요\.$/, '요')} — ${wt[1]}초 동안(기다리는 동안 바퀴가 계속 돌아요).`}); i += 2; continue; }
                lines.push({depth: r.depth, text: pair}); i += 1; continue;
            }
            // 점프: y 올리기 → 기다리기 → y 내리기
            const up = /^y 좌표를 (\d+(?:\.\d+)?)만큼 바꾸기$/.exec(r.label);
            const wt2 = nx ? /^(\S+)초 기다리기$/.exec(nx.label) : null;
            const dn = nx2 ? /^y 좌표를 -(\d+(?:\.\d+)?)만큼 바꾸기$/.exec(nx2.label) : null;
            if (up && wt2 && dn && up[1] === dn[1]) { lines.push({depth: r.depth, text: `위로 ${up[1]}만큼 올라갔다가 ${wt2[1]}초 뒤에 내려와요(점프).`}); i += 2; continue; }
            lines.push({depth: r.depth, text: conj(r.label)});
        }
    });
    const tail = [];
    const ops = opsAll(ir);
    if (has(ops, /^cubroid(?:dcmotor|led|master|sound|proximity|lighttouch)/)) tail.push('로봇 블록은 로봇을 연결해야 실제로 움직여요(블록 목록 위 주황색 연결 버튼).');
    if (has(ops, /^(?:cubroidcolor|cubroidobject|facerecognition|handpose2scratch|posenet2scratch|ic2scratch|ml2scratch)_/)) tail.push('카메라 블록은 처음에 카메라를 켜고 AI 모델을 불러오느라 몇 초 걸려요.');
    return {title: '방금 만든 블록은 이렇게 동작해요', lines, tail};
};
const explainEn = ir => {
    const lines = [];
    core.describe(ir).forEach((rows, si) => {
        if (ir.scripts.length > 1) lines.push({depth: 0, text: `Script ${si + 1}`, head: true});
        rows.forEach((r, i) => {
            const s = String(r.label || '');
            let t;
            if (i === 0) t = /green flag/.test(s) ? 'Starts when you click the green flag.' : `Starts: ${s}.`;
            else if (s === 'forever') t = 'From here on it repeats until you press stop:';
            else if (/^repeat until/.test(s)) t = `Repeats until ${s.replace(/^repeat until\s*/, '')}:`;
            else if (/^repeat \d+$/.test(s)) t = `Repeats ${s.replace(/^repeat /, '')} times:`;
            else if (/^if .* then$/.test(s)) t = `If ${s.replace(/^if\s*/, '').replace(/\s*then$/, '')}:`;
            else if (s === 'else') t = 'Otherwise:';
            else t = `${s.charAt(0).toUpperCase()}${s.slice(1)}.`;
            lines.push({depth: r.depth, text: t});
        });
    });
    const tail = [];
    if (has(opsAll(ir), /^cubroid(?:dcmotor|led|master|sound|proximity|lighttouch)/)) tail.push('Robot blocks only move the real robot after you connect it (orange connect button above the block list).');
    return {title: 'Here is how the blocks you just made work', lines, tail};
};
const explainIr = (ir, lang) => (lang === 'en' ? explainEn(ir) : explainKo(ir));

module.exports = {suggestChips, explainIr, isExplainRequest, CANDS};
